/**
 * Shared “Remove from this PO” rules for Invoices + GR Match.
 *
 * One action for:
 * - soft-link (`attachedToPO` / on order.attachedInvoices)
 * - assigned / suggested PO only (`pending` with PO or matched POs)
 *
 * Blocked for reconciled / sent to Xero (no casual undo in v1).
 * Venue users may use this (not admin-only).
 */

export const REMOVE_FROM_PO_KIND = {
  NONE: 'none',
  BLOCKED: 'blocked',
  SOFT_LINK: 'soft_link',
  ASSIGNED_PO: 'assigned_po',
}

/** Lifecycles that must not be cleared by Remove from this PO (v1). */
export const REMOVE_FROM_PO_BLOCKED_STATUSES = ['reconciled', 'sentToXero']

export const REMOVE_FROM_PO_BUTTON_LABEL = 'Remove from this PO'

/** Match modal label — same clear action, recommendation-focused wording. */
export const REMOVE_FROM_PO_MATCH_MODAL_LABEL = 'Not this order'

export const REMOVE_FROM_PO_CONFIRM = {
  soft_link:
    'Remove this invoice from the purchase order? It will stay uploaded. The PO link will be cleared on Invoices and Goods Receipting, and Matching will refresh.',
  assigned_po:
    'Clear this purchase order from the invoice? It will stay uploaded. Matching and Goods Receipting will refresh so this PO no longer treats it as a match candidate.',
}

const CLEAR_FIELD_KEYS = [
  'purchaseOrderNumber',
  'linkedOrderId',
  'matchedOrderIds',
  'matchedPurchaseOrderNumbers',
  'matchSummary.purchaseOrderNumber',
]

function normalisePo(value) {
  return String(value || '').trim()
}

export function getInvoiceLifecycleStatus(invoice = {}) {
  return String(invoice?.status || 'pending').trim() || 'pending'
}

/**
 * True when the invoice carries a PO assignment or match touch — even if not
 * soft-linked on the order yet (the ABC / wrong-PO pending case).
 */
export function invoiceHasAssignedOrMatchedPo(invoice = {}) {
  if (normalisePo(invoice.purchaseOrderNumber)) return true
  if (invoice.linkedOrderId) return true

  const matchedPos = Array.isArray(invoice.matchedPurchaseOrderNumbers)
    ? invoice.matchedPurchaseOrderNumbers.map(normalisePo).filter(Boolean)
    : []
  if (matchedPos.length) return true

  if (normalisePo(invoice.matchSummary?.purchaseOrderNumber)) return true

  const matchedIds = Array.isArray(invoice.matchedOrderIds)
    ? invoice.matchedOrderIds.filter(Boolean)
    : []
  if (matchedIds.length) return true

  return false
}

export function isInvoiceSoftLinkedForRemove(invoice = {}) {
  const lifecycle = getInvoiceLifecycleStatus(invoice)
  if (lifecycle === 'attachedToPO') return true
  if (invoice.isLinkedToPO) return true
  return false
}

export function isRemoveFromPoBlocked(invoice = {}) {
  return REMOVE_FROM_PO_BLOCKED_STATUSES.includes(
    getInvoiceLifecycleStatus(invoice),
  )
}

export function getRemoveFromPoBlockReason(invoice = {}) {
  const lifecycle = getInvoiceLifecycleStatus(invoice)
  if (lifecycle === 'sentToXero') {
    return 'This invoice has been sent to Xero and cannot be removed from the PO here.'
  }
  if (lifecycle === 'reconciled') {
    return 'This invoice is reconciled and cannot be removed from the PO here.'
  }
  return ''
}

/**
 * What the backend / UI should clear when Remove from this PO runs.
 * Pure plan — Step 2 wires the API.
 */
export function getRemoveFromPoClearPlan(invoice = {}) {
  const meta = getRemoveFromPoActionMeta(invoice)
  if (!meta.canShow) {
    return {
      kind: meta.kind,
      clearOrderAttach: false,
      resetLifecycleToPending: false,
      clearFieldKeys: [],
      rematchAfter: false,
    }
  }

  return {
    kind: meta.kind,
    // Soft-link always pulls from Order.attachedInvoices; assigned-only may
    // still be on an order in edge cases — API should pull if present.
    clearOrderAttach: true,
    resetLifecycleToPending: meta.kind === REMOVE_FROM_PO_KIND.SOFT_LINK,
    clearFieldKeys: [...CLEAR_FIELD_KEYS],
    rematchAfter: true,
  }
}

/**
 * Shared view-model for Invoices row, Match modal, and GR Match.
 *
 * @returns {{
 *   kind: 'none'|'blocked'|'soft_link'|'assigned_po',
 *   canShow: boolean,
 *   buttonLabel: string,
 *   matchModalLabel: string,
 *   confirmCopy: string,
 *   blockReason: string,
 *   purchaseOrderNumber: string,
 * }}
 */
export function getRemoveFromPoActionMeta(invoice = {}) {
  if (invoice?.isArchived) {
    return {
      kind: REMOVE_FROM_PO_KIND.NONE,
      canShow: false,
      buttonLabel: REMOVE_FROM_PO_BUTTON_LABEL,
      matchModalLabel: REMOVE_FROM_PO_MATCH_MODAL_LABEL,
      confirmCopy: '',
      blockReason: '',
      purchaseOrderNumber: normalisePo(invoice.purchaseOrderNumber),
    }
  }

  if (isRemoveFromPoBlocked(invoice)) {
    return {
      kind: REMOVE_FROM_PO_KIND.BLOCKED,
      canShow: false,
      buttonLabel: REMOVE_FROM_PO_BUTTON_LABEL,
      matchModalLabel: REMOVE_FROM_PO_MATCH_MODAL_LABEL,
      confirmCopy: '',
      blockReason: getRemoveFromPoBlockReason(invoice),
      purchaseOrderNumber: normalisePo(invoice.purchaseOrderNumber),
    }
  }

  const po =
    normalisePo(invoice.purchaseOrderNumber) ||
    normalisePo(invoice.matchSummary?.purchaseOrderNumber) ||
    (Array.isArray(invoice.matchedPurchaseOrderNumbers)
      ? normalisePo(invoice.matchedPurchaseOrderNumbers[0])
      : '')

  if (isInvoiceSoftLinkedForRemove(invoice)) {
    return {
      kind: REMOVE_FROM_PO_KIND.SOFT_LINK,
      canShow: true,
      buttonLabel: REMOVE_FROM_PO_BUTTON_LABEL,
      matchModalLabel: REMOVE_FROM_PO_MATCH_MODAL_LABEL,
      confirmCopy: REMOVE_FROM_PO_CONFIRM.soft_link,
      blockReason: '',
      purchaseOrderNumber: po,
    }
  }

  if (invoiceHasAssignedOrMatchedPo(invoice)) {
    return {
      kind: REMOVE_FROM_PO_KIND.ASSIGNED_PO,
      canShow: true,
      buttonLabel: REMOVE_FROM_PO_BUTTON_LABEL,
      matchModalLabel: REMOVE_FROM_PO_MATCH_MODAL_LABEL,
      confirmCopy: REMOVE_FROM_PO_CONFIRM.assigned_po,
      blockReason: '',
      purchaseOrderNumber: po,
    }
  }

  return {
    kind: REMOVE_FROM_PO_KIND.NONE,
    canShow: false,
    buttonLabel: REMOVE_FROM_PO_BUTTON_LABEL,
    matchModalLabel: REMOVE_FROM_PO_MATCH_MODAL_LABEL,
    confirmCopy: '',
    blockReason: '',
    purchaseOrderNumber: '',
  }
}

export function canShowRemoveFromPoAction(invoice = {}) {
  return getRemoveFromPoActionMeta(invoice).canShow === true
}

/**
 * Match modal: show Not this order when the invoice can be cleared and a PO
 * is selected or recommended (wrong recommendation is fixable in-modal).
 */
export function canShowNotThisOrderInMatchModal(
  invoice = {},
  { selectedPurchaseOrderNumber, recommendedPurchaseOrderNumber } = {},
) {
  if (!canShowRemoveFromPoAction(invoice)) return false
  if (normalisePo(selectedPurchaseOrderNumber)) return true
  if (normalisePo(recommendedPurchaseOrderNumber)) return true
  return invoiceHasAssignedOrMatchedPo(invoice)
}
